import type { CSSProperties, ReactNode } from 'react'
import { CalcBadge } from './CalcBadge'

interface CalcPanelProps {
  title?: ReactNode
  sub?: string
  /** Optional "deterministic · Nms" footnote text rendered right-aligned in header. */
  meta?: string
  children: ReactNode
  style?: CSSProperties
}

/**
 * Neutral panel for deterministic calculation (balance projection, coverage delta).
 * Header has CalcBadge + optional meta footnote.
 * Must NOT carry indigo chrome — warm cream surface only (Rule 07).
 */
export function CalcPanel({ title, sub, meta, children, style }: CalcPanelProps): JSX.Element {
  return (
    <div
      data-testid="calc-panel"
      data-register="calculated"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '10px 14px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface-2)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
          <CalcBadge />
          {title != null && (
            <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
          )}
          {sub != null && (
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{sub}</span>
          )}
        </div>
        {meta != null && (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--text-3)',
              letterSpacing: '0.01em',
            }}
          >
            {meta}
          </span>
        )}
      </div>
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  )
}
